import { Vector3 } from "three";
import { CYLINDER_FIT_CONFIG, refineCylinderGeometric } from "./cylinder-fit";
import { PipeFitNeighborhood } from "./pipe-fit-neighborhood";
import { fitPipeSeed, type PipeSeedDiagnostic } from "./pipe-fit-seed";
import type { CylinderSurface } from "./cylinder-surface";
import type { CylinderFitDiagnostic } from "./cylinder-refinement";

type PointTuple = [number, number, number];
type ModelRecord = {
  center: PointTuple;
  axis: PointTuple;
  radial: PointTuple;
  radius: number;
};
export type CylinderFitReplay = {
  version: 1;
  points: PointTuple[];
  seed: PointTuple;
  spacing?: number;
  initial?: ModelRecord;
  diagnostic?: CylinderFitDiagnostic;
};

const MAX_CAPTURE_FRAMES = 8;
let recording: { frames: CylinderFitReplay[] } | undefined;

function modelRecord(model: CylinderSurface): ModelRecord {
  return {
    center: model.center.toArray(),
    axis: model.axis.toArray(),
    radial: model.radial.toArray(),
    radius: model.radius,
  };
}

/** @returns Whether the user explicitly enabled local point-window recording. */
export function isCylinderFitCaptureActive(): boolean {
  return !!recording;
}

/**
 * @param points Borrowed bounded fitting samples; copied only when capture is active.
 * @param seed Original click location.
 * @param initial Input fit for this attempt.
 * @param diagnostic Fit result summary.
 */
export function recordCylinderFitCapture(
  points: readonly Vector3[],
  seed: Vector3,
  initial: CylinderSurface,
  diagnostic: CylinderFitDiagnostic,
): void {
  if (!recording) return;
  recording.frames.push({
    version: 1,
    points: points
      .slice(0, CYLINDER_FIT_CONFIG.maximumSamples)
      .map((point) => point.toArray()),
    seed: seed.toArray(),
    initial: modelRecord(initial),
    diagnostic,
  });
  if (recording.frames.length > MAX_CAPTURE_FRAMES) recording.frames.shift();
}

/** @returns Explicitly activated bounded local capture with stop/download controls; no automatic uploads. */
export function captureCylinderFits(): {
  stop(): CylinderFitReplay[];
  download(): void;
} {
  const capture: { frames: CylinderFitReplay[] } = { frames: [] };
  recording = capture;
  function stop(): CylinderFitReplay[] {
    if (recording === capture) recording = undefined;
    return capture.frames;
  }
  return {
    stop,
    download(): void {
      const frames = stop();
      const url = URL.createObjectURL(
        new Blob([JSON.stringify({ version: 1, frames }, null, 2)], {
          type: "application/json",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = "cylinder-fit-capture.json";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  };
}

function isPoint(value: unknown): value is PointTuple {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every(
      (coordinate) =>
        typeof coordinate === "number" && Number.isFinite(coordinate),
    )
  );
}

/**
 * @param positions Borrowed PLY position attribute; never modified.
 * @param seed User-provided world coordinate.
 * @param radius Explicit seed-neighborhood radius in metres.
 * @returns Spatially downsampled replay input and sampling metadata, bounded independently of source size.
 */
export function cropCylinderFitReplay(
  positions: {
    count: number;
    getX(index: number): number;
    getY(index: number): number;
    getZ(index: number): number;
  },
  seed: PointTuple,
  radius: number,
): {
  replay: CylinderFitReplay;
  sourceCount: number;
  neighborhoodCount: number;
  voxelSize: number;
} {
  if (!isPoint(seed) || !Number.isFinite(radius) || radius <= 0) {
    throw new Error("Invalid replay crop");
  }
  let voxelSize = radius / Math.sqrt(CYLINDER_FIT_CONFIG.maximumSamples);
  for (;;) {
    const cells = new Map<string, { point: PointTuple; distance: number }>();
    let neighborhoodCount = 0;
    for (let index = 0; index < positions.count; index++) {
      const point: PointTuple = [
        positions.getX(index),
        positions.getY(index),
        positions.getZ(index),
      ];
      if (!point.every(Number.isFinite)) continue;
      const offset = point.map((value, axis) => value - seed[axis]);
      if (
        offset.reduce((sum, value) => sum + value * value, 0) >
        radius * radius
      ) {
        continue;
      }
      neighborhoodCount++;
      const cell = offset.map((value) => Math.floor(value / voxelSize));
      const key = cell.join(",");
      const distance = offset.reduce(
        (sum, value, axis) =>
          sum + (value - (cell[axis] + 0.5) * voxelSize) ** 2,
        0,
      );
      const previous = cells.get(key);
      if (!previous || distance < previous.distance) {
        cells.set(key, { point, distance });
      }
      if (cells.size > CYLINDER_FIT_CONFIG.maximumSamples) break;
    }
    if (cells.size <= CYLINDER_FIT_CONFIG.maximumSamples) {
      return {
        replay: {
          version: 1,
          points: [...cells.values()].map((cell) => cell.point),
          seed,
        },
        sourceCount: positions.count,
        neighborhoodCount,
        voxelSize,
      };
    }
    voxelSize *= 2;
  }
}

/**
 * @param input Untrusted local replay JSON.
 * @returns Validated bounded numerical input.
 */
export function parseCylinderFitReplay(input: unknown): CylinderFitReplay {
  if (
    typeof input !== "object" ||
    input === null ||
    !("version" in input) ||
    input.version !== 1 ||
    !("points" in input) ||
    !Array.isArray(input.points) ||
    input.points.length > CYLINDER_FIT_CONFIG.maximumSamples ||
    !input.points.every(isPoint) ||
    !("seed" in input) ||
    !isPoint(input.seed)
  ) {
    throw new Error("Invalid or oversized cylinder replay");
  }
  let spacing: number | undefined;
  if ("spacing" in input && input.spacing !== undefined) {
    if (
      typeof input.spacing !== "number" ||
      !Number.isFinite(input.spacing) ||
      input.spacing <= 0
    ) {
      throw new Error("Invalid point spacing");
    }
    ({ spacing } = input);
  }
  let initial: ModelRecord | undefined;
  if ("initial" in input && input.initial !== undefined) {
    const model = input.initial;
    if (
      typeof model !== "object" ||
      model === null ||
      !("center" in model) ||
      !isPoint(model.center) ||
      !("axis" in model) ||
      !isPoint(model.axis) ||
      !("radial" in model) ||
      !isPoint(model.radial) ||
      !("radius" in model) ||
      typeof model.radius !== "number" ||
      !Number.isFinite(model.radius) ||
      model.radius <= 0
    ) {
      throw new Error("Invalid initial cylinder");
    }
    initial = {
      center: model.center,
      axis: model.axis,
      radial: model.radial,
      radius: model.radius,
    };
  }
  return {
    version: 1,
    points: input.points,
    seed: input.seed,
    spacing,
    initial,
  };
}

/**
 * @param input Bounded validated replay input, optionally with a warm-start cylinder.
 * @returns Serializable result/quality for the maintained numerical backend.
 */
export async function replayCylinderFit(
  input: CylinderFitReplay,
): Promise<object> {
  const validated = parseCylinderFitReplay(input);
  const points = validated.points.map((point) =>
    new Vector3().fromArray(point),
  );
  const neighborhood = new PipeFitNeighborhood(points);
  const spacing = validated.spacing ?? neighborhood.spacing();
  if (!spacing) {
    return { accepted: false, reason: "insufficient-spacing-evidence" };
  }
  const started = performance.now();
  const seedDiagnostics: PipeSeedDiagnostic[] = [];
  const result = validated.initial
    ? await refineCylinderGeometric(
        points,
        {
          center: new Vector3().fromArray(validated.initial.center),
          axis: new Vector3().fromArray(validated.initial.axis),
          radial: new Vector3().fromArray(validated.initial.radial),
          radius: validated.initial.radius,
        },
        { spacing },
      )
    : await fitPipeSeed(
        neighborhood,
        new Vector3().fromArray(validated.seed),
        () => false,
        {
          spacing,
          onDiagnostic: (diagnostic) => seedDiagnostics.push(diagnostic),
        },
      );
  if (!result) {
    return {
      accepted: false,
      reason: "seed-not-cylindrical",
      spacing,
      seedDiagnostics,
    };
  }
  return {
    ...result,
    cylinder: modelRecord(result.cylinder),
    elapsedMs: performance.now() - started,
    spacing,
    spacingSource: validated.spacing ? "supplied" : "retained-sample-estimate",
    config: CYLINDER_FIT_CONFIG,
    seedDiagnostics,
  };
}
